/**
 * What Bailey sees: a Team's backlog filter kept exactly as written, and
 * Jira's answer under it (Andres, 2026-10-09: a pasted JQL was paraphrased,
 * nothing showed what it matched, and `label` for `labels` failed silently).
 *
 * One component in three places: Bailey's Backlog field in Team settings,
 * the What Bailey sees sheet off the queue header, and Add a team. It asks
 * `POST /api/desks/:id/backlog/preview` (or `/api/desks/backlog/preview` for
 * a Team being created) 400 ms after the last keystroke, and renders the
 * answer: a count with when it was checked, the field Jira refused with a
 * one-click fix, or a plain "0 issues match", then the first five matches.
 * A pasted link to a Jira ticket under an epic reads as the epic, and says
 * how to work on just that ticket (Add tickets, prefilled, on a Team).
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { BacklogPreviewResponse } from "@yagni/shared";
import { captureUiError } from "../sentry.js";
import { heldBacklogLine } from "./copy.js";

export type BacklogSource = "jira" | "linear";

export interface BacklogPreviewState {
  answer: BacklogPreviewResponse | null;
  checking: boolean;
  failed: boolean;
  recheck: () => void;
}

const DEBOUNCE_MS = 400;

function emptyAnswer(filter: string): BacklogPreviewResponse {
  return {
    filter,
    kind: "empty",
    total: null,
    checkedAt: new Date().toISOString(),
    rows: [],
    more: 0,
  };
}

export function useBacklogPreview(
  deskId: string | null,
  filter: string,
  source: BacklogSource | undefined,
  options: { debounceMs?: number; enabled?: boolean } = {},
): BacklogPreviewState {
  const debounceMs = options.debounceMs ?? DEBOUNCE_MS;
  const enabled = options.enabled ?? true;
  const [answer, setAnswer] = useState<BacklogPreviewResponse | null>(null);
  const [checking, setChecking] = useState(false);
  const [failed, setFailed] = useState(false);
  const [nonce, setNonce] = useState(0);
  const asked = useRef(0);
  const first = useRef(true);
  const forced = useRef(false);

  useEffect(() => {
    if (!enabled) return;
    const ask = ++asked.current;
    if (!filter.trim()) {
      first.current = false;
      setAnswer(emptyAnswer(filter));
      setChecking(false);
      setFailed(false);
      return;
    }
    const delay = first.current || forced.current ? 0 : debounceMs;
    first.current = false;
    forced.current = false;
    setChecking(true);
    const timer = setTimeout(() => {
      const url = deskId
        ? `/api/desks/${encodeURIComponent(deskId)}/backlog/preview`
        : "/api/desks/backlog/preview";
      fetch(url, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(source ? { filter, source } : { filter }),
      })
        .then((res) => {
          if (!res.ok) throw new Error(`backlog preview failed (${res.status})`);
          return res.json() as Promise<BacklogPreviewResponse>;
        })
        .then((body) => {
          if (ask !== asked.current) return;
          setAnswer(body && typeof body.kind === "string" ? body : null);
          setFailed(!(body && typeof body.kind === "string"));
          setChecking(false);
        })
        .catch((err: unknown) => {
          if (ask !== asked.current) return;
          captureUiError(err, {
            area: "Teams",
            operation: "backlog_preview",
            deskId: deskId ?? undefined,
            severity: "warning",
          });
          setFailed(true);
          setChecking(false);
        });
    }, delay);
    return () => clearTimeout(timer);
  }, [deskId, filter, source, enabled, debounceMs, nonce]);

  const recheck = useCallback(() => {
    forced.current = true;
    setNonce((n) => n + 1);
  }, []);

  return { answer, checking, failed, recheck };
}

/** "just now", "4 min ago", else the time it was checked. */
export function checkedWords(iso: string | null | undefined, now: number = Date.now()): string {
  if (!iso) return "just now";
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return "just now";
  const minutes = Math.floor((now - at.getTime()) / 60_000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} min ago`;
  return at.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
}

function escapeRegex(text: string): string {
  return text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/** The filter with a refused field swapped for the one Jira takes, before its operator. */
export function fixFilterField(filter: string, field: string, suggestion: string): string {
  const pattern = new RegExp(
    `(^|[\\s(])${escapeRegex(field)}(\\s*(?:!=|!~|=|~|\\bin\\b|\\bnot\\b|\\bis\\b))`,
    "gi",
  );
  return filter.replace(pattern, (_match, lead: string, op: string) => `${lead}${suggestion}${op}`);
}

const DARK_FACT =
  "Until this parses Bailey has nothing to pick from, and the Team reads as dark. It will not fall back to anything else.";

export interface BacklogPreviewProps {
  deskId: string | null;
  value: string;
  onChange?: (next: string) => void;
  readOnly?: boolean;
  source?: BacklogSource;
  /** How many Bailey picks a day, for the "and N more" line. */
  cap?: number | null;
  teamName?: string;
  onAnswer?: (answer: BacklogPreviewResponse | null) => void;
  testId?: string;
  /** A preview state the parent already holds (the sheet's Check now drives it). */
  state?: BacklogPreviewState;
  /** Add just KEY would leave unsaved edits behind; say so instead of leaving. */
  addJustBlocked?: boolean;
  /**
   * Once the tracker accepts the typed text, fold the box into one line naming
   * what is held, with Change to bring it back. The text stays with the parent.
   */
  foldOnAccept?: boolean;
}

/** The answers that mean the tracker read the text and holds a backlog. */
const ACCEPTED_KINDS: ReadonlyArray<BacklogPreviewResponse["kind"]> = [
  "jql",
  "filter_link",
  "keys",
  "linear_view",
];

export function BacklogPreview(props: BacklogPreviewProps) {
  const own = useBacklogPreview(props.deskId, props.value, props.source, {
    enabled: props.state == null,
  });
  return <BacklogPreviewView {...props} state={props.state ?? own} />;
}

function BacklogPreviewView({
  deskId,
  value,
  onChange,
  readOnly,
  source,
  cap,
  onAnswer,
  testId,
  state,
  addJustBlocked,
  foldOnAccept,
}: BacklogPreviewProps & { state: BacklogPreviewState }) {
  const { answer, checking, failed, recheck } = state;
  const reported = useRef<BacklogPreviewResponse | null | undefined>(undefined);
  useEffect(() => {
    if (reported.current === answer) return;
    reported.current = answer;
    onAnswer?.(answer);
  }, [answer, onAnswer]);

  // Change is a one-way override until the text changes: a new paste re-folds
  // once the tracker accepts it, and a stale answer for edited text never does.
  const [unfolded, setUnfolded] = useState(false);
  // A fold mid-typing would take the box away from someone still writing a
  // longer query, so an answer waits for the box to lose focus.
  const [typing, setTyping] = useState(false);
  const box = useRef<HTMLTextAreaElement | null>(null);
  const refocus = useRef(false);
  useEffect(() => {
    setUnfolded(false);
  }, [value]);

  // The count as the answer block shows it: an accepted kind that answered
  // `total: null` with no readable rows is the amber "0 issues match" case.
  const shownTotal = answer
    ? (answer.total ?? answer.rows.filter((row) => !row.missing).length)
    : 0;
  const folded =
    foldOnAccept === true &&
    readOnly !== true &&
    onChange != null &&
    !checking &&
    !failed &&
    answer != null &&
    answer.filter === value &&
    ACCEPTED_KINDS.includes(answer.kind) &&
    shownTotal !== 0 &&
    !unfolded &&
    !typing;

  useEffect(() => {
    if (folded || !refocus.current) return;
    refocus.current = false;
    box.current?.focus();
  }, [folded]);

  return (
    <div className="blpick" data-testid={testId ?? "backlog-preview"}>
      {readOnly ? (
        <div className="blecho">
          <span className="label">The filter, as written</span>
          <code>{value}</code>
        </div>
      ) : folded && answer ? (
        <div className="blecho fold">
          <span className="held" data-testid="backlog-preview-held">
            {heldBacklogLine(answer, source)}
          </span>
          <button
            type="button"
            className="ref"
            onClick={() => {
              refocus.current = true;
              setUnfolded(true);
            }}
          >
            Change
          </button>
        </div>
      ) : (
        <textarea
          ref={box}
          className="blq"
          rows={2}
          spellCheck={false}
          aria-label="Backlog filter"
          placeholder="labels = yagni AND project = UPO · or a filter link · or UPO-418, UPO-421"
          value={value}
          onFocus={() => setTyping(true)}
          onBlur={() => setTyping(false)}
          onChange={(event) => onChange?.(event.target.value)}
        />
      )}
      <div className="blout" aria-live="polite">
        <PreviewOut
          deskId={deskId}
          answer={answer}
          value={value}
          checking={checking}
          failed={failed}
          recheck={recheck}
          readOnly={readOnly === true}
          cap={cap ?? null}
          onFix={(next) => onChange?.(next)}
          addJustBlocked={addJustBlocked === true}
          folded={folded}
        />
      </div>
    </div>
  );
}

/**
 * A pasted ticket read as its epic: what happened, and how to work on just
 * that ticket. On a Team, a quiet action opens Add tickets with the ticket
 * already searched (`?add=1&q=KEY`); the person still presses Add there.
 * Before the Team exists, the same thing in words. The action leaves the
 * settings sheet, so while it holds other unsaved edits the action waits and
 * says why rather than dropping them.
 */
function PastedTicket({
  deskId,
  note,
  ticket,
  blocked,
}: {
  deskId: string | null;
  note: string | null;
  ticket: string;
  blocked: boolean;
}) {
  return (
    <div className="blst quiet" data-testid="backlog-preview-pasted">
      {note}
      {deskId ? (
        <>
          <AddJustTicket deskId={deskId} ticket={ticket} blocked={blocked} />
          {blocked && (
            <span
              style={{ display: "block", marginTop: 4 }}
              data-testid="backlog-preview-add-just-blocked"
            >
              Add just {ticket} leaves these settings. Save or discard your other changes first.
            </span>
          )}
        </>
      ) : (
        <span style={{ display: "block", marginTop: 4 }}>
          To work on just {ticket}, create the Team, then use Add tickets.
        </span>
      )}
    </div>
  );
}

function AddJustTicket({
  deskId,
  ticket,
  blocked,
}: {
  deskId: string;
  ticket: string;
  blocked: boolean;
}) {
  const navigate = useNavigate();
  return (
    <button
      type="button"
      className="ref"
      data-testid="backlog-preview-add-just"
      disabled={blocked}
      onClick={() =>
        navigate(`/desks/${encodeURIComponent(deskId)}?add=1&q=${encodeURIComponent(ticket)}`)
      }
    >
      Add just {ticket}
    </button>
  );
}

function PreviewOut({
  deskId,
  answer,
  value,
  checking,
  failed,
  recheck,
  readOnly,
  cap,
  onFix,
  addJustBlocked,
  folded,
}: {
  deskId: string | null;
  answer: BacklogPreviewResponse | null;
  value: string;
  checking: boolean;
  failed: boolean;
  recheck: () => void;
  readOnly: boolean;
  cap: number | null;
  onFix: (next: string) => void;
  addJustBlocked: boolean;
  /** The box is folded to its line: only what the line cannot say stays. */
  folded: boolean;
}) {
  if (failed && !checking) {
    return (
      <div className="blst warn">
        Could not ask Jira just now.
        <button type="button" className="ref" onClick={recheck}>
          Check again
        </button>
      </div>
    );
  }
  if (!answer) {
    return checking ? <div className="blst quiet">Asking Jira…</div> : null;
  }
  switch (answer.kind) {
    case "empty":
      return (
        <div className="blst quiet">
          Paste JQL, a Jira filter or board link, or issue keys. It is kept as written. Nothing is
          paraphrased.
        </div>
      );
    case "partial":
      return (
        <div className="blst quiet">Keep typing. Jira is asked as soon as the filter parses.</div>
      );
    case "unsupported":
      return (
        <div className="blst warn">
          {answer.error?.message ?? "That is not a filter Jira or Linear can answer."}
        </div>
      );
    case "error": {
      const error = answer.error;
      return (
        <div className="blst err" role="alert">
          <b>Jira says:</b>{" "}
          {error?.field ? (
            <>
              the field <code>{error.field}</code> does not exist, or you cannot read it.
              {error.suggestion && !readOnly && (
                <button
                  type="button"
                  className="ref"
                  data-testid="backlog-preview-fix"
                  onClick={() => onFix(fixFilterField(value, error.field!, error.suggestion!))}
                >
                  Use {error.suggestion}
                </button>
              )}
            </>
          ) : (
            (error?.message ?? "it could not run this filter.")
          )}
          <span className="fact" style={{ display: "block", marginTop: 4 }}>
            {DARK_FACT}
          </span>
        </div>
      );
    }
    default:
      break;
  }

  // The line names what is held; the pasted-ticket note and its warning are
  // the one thing it cannot say, so they outlive the fold.
  if (folded) {
    return answer.pastedIssue ? (
      <PastedTicket
        deskId={deskId}
        note={answer.note ?? null}
        ticket={answer.pastedIssue.key}
        blocked={addJustBlocked}
      />
    ) : null;
  }

  const total = answer.total ?? answer.rows.filter((row) => !row.missing).length;
  return (
    <>
      {answer.resolved && (
        <div className="blecho">
          <span className="label">As Jira runs it</span>
          <code>{answer.resolved.jql}</code>
        </div>
      )}
      {total === 0 ? (
        <div className="blst warn">
          <b>0 issues match.</b> Bailey has nothing to pick from, and the Team reads as dark until
          something does.
        </div>
      ) : (
        <div className="blst ok" data-testid="backlog-preview-count">
          <b>
            {total} issue{total === 1 ? "" : "s"} match
          </b>{" "}
          right now
          {answer.resolved ? ` · the link is Jira ${answer.resolved.name}, run as its JQL` : ""}
          {answer.kind === "keys" ? " · exactly these, nothing else" : ""}
          {` · checked ${checkedWords(answer.checkedAt)}`}
        </div>
      )}
      {answer.pastedIssue && (
        <PastedTicket
          deskId={deskId}
          note={answer.note ?? null}
          ticket={answer.pastedIssue.key}
          blocked={addJustBlocked}
        />
      )}
      {answer.rows.length > 0 && (
        <div className="blrows">
          {answer.rows.slice(0, 5).map((row) => (
            <div
              key={row.key}
              className={row.missing ? "blrow miss" : "blrow"}
              data-testid="backlog-preview-row"
            >
              <span className="k">{row.key}</span>
              <span className="t">
                {row.title || (row.missing ? "Not found, or the connection cannot read it" : "")}
              </span>
              <span className="tags">
                {row.labels.map((label) => (
                  <span key={label} className="chip">
                    {label}
                  </span>
                ))}
                {row.epic && <span className="chip">{row.epic}</span>}
                {row.inQueue && (
                  <span className="chip" style={{ color: "var(--accent-active)" }}>
                    in the queue
                  </span>
                )}
              </span>
            </div>
          ))}
          {answer.more > 0 && (
            <div className="fact" style={{ padding: "6px 8px 2px" }}>
              and {answer.more} more in Jira
              {cap != null ? ` · Bailey picks the ${cap} most agent-ready each morning` : ""}
            </div>
          )}
        </div>
      )}
    </>
  );
}
